import Api from "../../../services/api";

export interface IAssetTypeGroup {
    id: string;
    name: string;
}

/**
 * Fetches asset type groups: descendants of GroupAssetTypeId (e.g., Vehicle, Trailer, Equipment,
 * Container), excluding the root itself.
 */
export function getAssetTypeGroups(api: Api): Promise<IAssetTypeGroup[]> {
    return new Promise((resolve, reject) => {
        api.call(
            "Get",
            {
                typeName: "Group",
                search: {
                    ancestors: [{ id: "GroupAssetTypeId" }]
                }
            },
            (groups: IAssetTypeGroup[]) => {
                const assetTypes = groups.filter(group => group.id !== "GroupAssetTypeId");
                resolve(assetTypes);
            },
            reject
        );
    });
}

/** Built-in asset-type groups whose inspections can be regulatory (DVIR-bearing). */
const REGULATORY_ASSET_TYPE_ROOTS = ["GroupVehicleId", "GroupTrailerId"];

/** Cached across callers; see getRegulatoryAssetTypeIds. */
let regulatoryAssetTypeIdsPromise: Promise<string[]> | null = null;

/**
 * Clears the getRegulatoryAssetTypeIds cache. Tests only — the set is a per-database group
 * topology that does not change within a session.
 */
export function resetRegulatoryAssetTypeIdsCache(): void {
    regulatoryAssetTypeIdsPromise = null;
}

function fetchRegulatoryAssetTypeIds(api: Api): Promise<string[]> {
    return new Promise((resolve, reject) => {
        api.call(
            "Get",
            {
                typeName: "Group",
                search: { ancestors: REGULATORY_ASSET_TYPE_ROOTS.map(id => ({ id })) }
            },
            (groups: IAssetTypeGroup[]) => {
                const ids = new Set(REGULATORY_ASSET_TYPE_ROOTS);
                for (const group of groups ?? []) {
                    if (group?.id) {
                        ids.add(group.id);
                    }
                }
                // An array, not the Set: this is what the Redux slice stores, and spreading a Set
                // needs ES2015 iteration which Drive's target does not permit. Callers that test
                // membership build their own Set from it.
                const unique: string[] = [];
                ids.forEach(id => unique.push(id));
                resolve(unique);
            },
            // Rejects so getRegulatoryAssetTypeIds owns the degraded fallback, and can keep it out of the cache.
            reject
        );
    });
}

/**
 * Every asset-type group whose inspections can be regulatory: the Vehicle and Trailer built-ins
 * and anything beneath them. A customer group nested under a built-in asset type counts as that
 * asset type server-side (InspectionTemplate.GetAssetTypeByGroups), so a fleet organised as
 * `Vehicle -> Heavy Trucks` must not lose its DVIR declarations.
 *
 * Asked as an `ancestors` query rather than derived from the groups getAssetTypeGroups returns.
 * The parent direction is not on the wire — Group.Parent is [ApiExclude] and GroupConverter does
 * not serialize it — and `children` cannot be added to IAssetTypeGroup because that type doubles
 * as a Zenith dropdown item, whose own `children` contract is incompatible.
 *
 * Returns the two built-ins alone on failure, so a lookup error narrows the rule rather than
 * disabling it.
 */
export function getRegulatoryAssetTypeIds(api: Api): Promise<string[]> {
    // Memoised: the group topology is fixed per database but this is called on every roadside
    // redirect and inspection page mount. Caching the promise also collapses concurrent callers.
    if (!regulatoryAssetTypeIdsPromise) {
        regulatoryAssetTypeIdsPromise = fetchRegulatoryAssetTypeIds(api).catch(() => {
            // The degraded set must not be cached: it silently excludes nested customer asset types,
            // so the next caller should retry. Clearing here rather than in the error callback matters:
            // api.call may invoke that callback synchronously, before the assignment above completes,
            // so a clear from inside would be overwritten.
            regulatoryAssetTypeIdsPromise = null;
            return REGULATORY_ASSET_TYPE_ROOTS.slice();
        });
    }
    return regulatoryAssetTypeIdsPromise;
}
